import {
  evaluateConditionalBehavior,
  evaluateCondition,
} from '@shared/policy-application/conditionalLogicUtils';
import { FormParameterValues } from '@client/hooks/useFormParameters';
import { validateRequiredQuestion } from '@client/util/questionValidation';
import { calculateCompositeValue } from '@shared/policy-application/compositeFieldUtils';

/**
 * Centralized service for evaluating form element visibility and conditional requirements
 * This eliminates duplication of conditional logic across the codebase
 */
export class FormConditionalService {
  private questionParentsCache: Map<
    string,
    {
      section: PolicyFormSection;
      subsection: PolicyFormSubSection;
      questionGroup: PolicyFormQuestionGroup;
    } | null
  > = new Map();

  constructor(
    private formDefinition: PolicyFormDefinition,
    private formData: PolicyFormSubmissionData,
    private parameters: FormParameterValues = {},
  ) {}

  /**
   * Updates the form data and parameters for visibility calculations
   */
  update(formData: PolicyFormSubmissionData, parameters: FormParameterValues = {}) {
    this.formData = formData;
    this.parameters = parameters;
  }

  /**
   * Checks if a section is visible
   */
  isSectionVisible(section: PolicyFormSection): boolean {
    const sectionEval = evaluateConditionalBehavior(
      section.conditional,
      this.formData,
      this.parameters,
    );
    return sectionEval.visible;
  }

  /**
   * Checks if a subsection is visible
   */
  isSubsectionVisible(subsection: PolicyFormSubSection, section?: PolicyFormSection): boolean {
    // If section is provided, check it first
    if (section && !this.isSectionVisible(section)) {
      return false;
    }

    const subsectionEval = evaluateConditionalBehavior(
      subsection.conditional,
      this.formData,
      this.parameters,
    );
    if (!subsectionEval.visible) return false;

    // A subsection is only visible if it has at least one visible question group
    return this.hasVisibleQuestionGroup(subsection);
  }

  /**
   * Checks if a subsection has at least one visible question group
   */
  hasVisibleQuestionGroup(subsection: PolicyFormSubSection): boolean {
    return subsection.questionGroups.some(group => {
      const groupEval = evaluateConditionalBehavior(
        group.conditional,
        this.formData,
        this.parameters,
      );
      if (!groupEval.visible) return false;

      // Check if at least one question in the group is visible
      return group.questions.some(questionRef => {
        const question = this.formDefinition.questions[questionRef];
        if (!question) return false;

        const questionEval = evaluateConditionalBehavior(
          question.conditional,
          this.formData,
          this.parameters,
        );
        return questionEval.visible;
      });
    });
  }

  /**
   * Checks if a question group is visible
   */
  isQuestionGroupVisible(
    group: PolicyFormQuestionGroup,
    subsection?: PolicyFormSubSection,
    section?: PolicyFormSection,
  ): boolean {
    // Check parent visibility if provided
    if (section && subsection && !this.isSubsectionVisible(subsection, section)) {
      return false;
    }

    const groupEval = evaluateConditionalBehavior(
      group.conditional,
      this.formData,
      this.parameters,
    );
    return groupEval.visible;
  }

  /**
   * Checks if a question is visible
   */
  isQuestionVisible(question: PolicyFormQuestion): boolean {
    const questionEval = evaluateConditionalBehavior(
      question.conditional,
      this.formData,
      this.parameters,
    );
    return questionEval.visible;
  }

  /**
   * Checks if a question is visible through it's questionRef
   */
  isQuestionRefVisible(questionRef: string): boolean {
    const question = this.formDefinition.questions[questionRef];
    if (!question) return false;
    return this.isQuestionVisible(question);
  }

  /**
   * Gets all visible sections with their visible subsections
   */
  getVisibleSections(): Array<{
    section: PolicyFormSection;
    subsections: PolicyFormSubSection[];
  }> {
    const visibleSections: Array<{
      section: PolicyFormSection;
      subsections: PolicyFormSubSection[];
    }> = [];

    for (const section of this.formDefinition.sections) {
      if (!this.isSectionVisible(section)) continue;

      const visibleSubsections: PolicyFormSubSection[] = [];

      for (const subsection of section.subsections) {
        if (this.isSubsectionVisible(subsection)) {
          visibleSubsections.push(subsection);
        }
      }

      if (visibleSubsections.length > 0) {
        visibleSections.push({ section, subsections: visibleSubsections });
      }
    }

    return visibleSections;
  }

  /**
   * Gets the total count of visible subsections
   */
  getTotalVisibleSubsections(): number {
    let count = 0;
    for (const section of this.formDefinition.sections) {
      if (!this.isSectionVisible(section)) continue;

      for (const subsection of section.subsections) {
        if (this.isSubsectionVisible(subsection)) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Gets visible question groups for a subsection
   */
  getVisibleQuestionGroups(subsection: PolicyFormSubSection): PolicyFormQuestionGroup[] {
    return subsection.questionGroups.filter(group => this.isQuestionGroupVisible(group));
  }

  /**
   * Builds the question parents lookup map lazily
   */
  private buildQuestionParentsMap(): void {
    if (this.questionParentsCache.size > 0) return; // Already built

    for (const section of this.formDefinition.sections) {
      for (const subsection of section.subsections) {
        for (const questionGroup of subsection.questionGroups) {
          for (const questionRef of questionGroup.questions) {
            this.questionParentsCache.set(questionRef, {
              section,
              subsection,
              questionGroup,
            });
          }
        }
      }
    }
  }

  /**
   * Finds the parent hierarchy for a given question - O(1) lookup after initial build
   */
  private findQuestionParents(questionRef: string): {
    section: PolicyFormSection;
    subsection: PolicyFormSubSection;
    questionGroup: PolicyFormQuestionGroup;
  } | null {
    this.buildQuestionParentsMap();
    return this.questionParentsCache.get(questionRef) || null;
  }

  /**
   * Gets complete conditional evaluation for a question (visibility + required state + text state)
   * Checks parent visibility (section, subsection, questionGroup) before evaluating question
   */
  getQuestionState(question: PolicyFormQuestion): {
    visible: boolean;
    required: boolean;
    showConditionalText: boolean;
  } {
    // Find the parent hierarchy for this question
    const parents = this.findQuestionParents(question.questionRef);
    if (!parents) {
      // If no parents found, the question is orphaned and should be hidden
      return { visible: false, required: false, showConditionalText: false };
    }

    // Check parent visibility in order: section → subsection → questionGroup
    if (!this.isSectionVisible(parents.section)) {
      return { visible: false, required: false, showConditionalText: false };
    }

    if (!this.isSubsectionVisible(parents.subsection, parents.section)) {
      return { visible: false, required: false, showConditionalText: false };
    }

    if (!this.isQuestionGroupVisible(parents.questionGroup, parents.subsection, parents.section)) {
      return { visible: false, required: false, showConditionalText: false };
    }

    // If all parents are visible, evaluate the question itself
    return evaluateConditionalBehavior(
      question.conditional,
      this.formData,
      this.parameters,
      question.required,
    );
  }

  /**
   * Gets required field evaluation for a question
   * @deprecated Use getQuestionState instead for complete evaluation
   */
  getQuestionRequirement(question: PolicyFormQuestion): {
    visible: boolean;
    required: boolean;
  } {
    return this.getQuestionState(question);
  }

  /**
   * Checks if a question is conditionally required (visible AND required)
   */
  isQuestionRequired(question: PolicyFormQuestion): boolean {
    const state = this.getQuestionState(question);
    return state.visible && state.required;
  }

  /**
   * Gets all required questions for a subsection
   */
  getRequiredQuestionsForSubsection(subsection: PolicyFormSubSection): PolicyFormQuestion[] {
    const requiredQuestions: PolicyFormQuestion[] = [];

    for (const group of subsection.questionGroups) {
      if (!this.isQuestionGroupVisible(group)) continue;

      for (const questionRef of group.questions) {
        const question = this.formDefinition.questions[questionRef];
        if (!question) continue;

        if (this.isQuestionRequired(question)) {
          requiredQuestions.push(question);
        }
      }
    }

    return requiredQuestions;
  }

  /**
   * Validates if a question's value is complete (handles both regular fields and tables)
   */
  private isQuestionValueComplete(
    question: PolicyFormQuestion,
    value: PolicyFormSubmissionDataValue,
  ): boolean {
    // For composite fields, check if there's a composite value even if the raw value is empty
    let effectiveValue = value;

    if (question.composite) {
      // Check if composite should be applied based on conditional logic
      const shouldApplyComposite = question.composite.conditional
        ? evaluateCondition(question.composite.conditional, this.formData, this.parameters)
        : true;

      if (shouldApplyComposite) {
        // For composite fields, calculate the composite value to check if field is actually filled
        const compositeValue = calculateCompositeValue(
          question,
          this.formData,
          this.formDefinition.questions,
          this.parameters,
        );

        // If there's a composite value, the field is not empty
        if (compositeValue !== undefined && compositeValue !== null && compositeValue !== '') {
          effectiveValue = compositeValue;
        }

        // For compositeFieldRef with allowAppendText, check the separate composite field
        if (question.composite.compositeFieldRef) {
          const compositeFieldValue = this.formData[question.composite.compositeFieldRef];
          if (
            compositeFieldValue !== undefined &&
            compositeFieldValue !== null &&
            compositeFieldValue !== ''
          ) {
            effectiveValue = compositeFieldValue;
          }
        }
      }
    }

    const validationResult = validateRequiredQuestion(question, effectiveValue);
    return validationResult.isValid;
  }

  /**
   * Gets all missing required fields for a subsection
   */
  getMissingRequiredFields(subsection: PolicyFormSubSection): {
    questionRef: string;
    question: PolicyFormQuestion;
  }[] {
    const missing: { questionRef: string; question: PolicyFormQuestion }[] = [];

    for (const group of subsection.questionGroups) {
      if (!this.isQuestionGroupVisible(group)) continue;

      for (const questionRef of group.questions) {
        const question = this.formDefinition.questions[questionRef];
        if (!question) continue;

        if (this.isQuestionRequired(question)) {
          const value = this.formData[question.questionRef];

          if (!this.isQuestionValueComplete(question, value)) {
            missing.push({ questionRef: question.questionRef, question });
          }
        }
      }
    }

    return missing;
  }

  /**
   * Validates if a subsection is complete (all required fields filled)
   */
  isSubsectionComplete(subsection: PolicyFormSubSection): boolean {
    const missingFields = this.getMissingRequiredFields(subsection);
    return missingFields.length === 0;
  }

  /**
   * Gets complete form validation state
   */
  getFormValidationState(): {
    isValid: boolean;
    missingRequiredFields: Array<{
      questionRef: string;
      question: PolicyFormQuestion;
      subsectionId: string;
      sectionId: string;
    }>;
    visibleQuestions: Set<string>;
    requiredQuestions: Set<string>;
  } {
    const missingRequiredFields: Array<{
      questionRef: string;
      question: PolicyFormQuestion;
      subsectionId: string;
      sectionId: string;
    }> = [];
    const visibleQuestions = new Set<string>();
    const requiredQuestions = new Set<string>();

    const visibleSections = this.getVisibleSections();

    for (const { section, subsections } of visibleSections) {
      for (const subsection of subsections) {
        for (const group of subsection.questionGroups) {
          if (!this.isQuestionGroupVisible(group)) continue;

          for (const questionRef of group.questions) {
            const question = this.formDefinition.questions[questionRef];
            if (!question) continue;

            const state = this.getQuestionState(question);

            if (state.visible) {
              visibleQuestions.add(question.questionRef);

              if (state.required) {
                requiredQuestions.add(question.questionRef);

                const value = this.formData[question.questionRef];

                if (!this.isQuestionValueComplete(question, value)) {
                  missingRequiredFields.push({
                    questionRef: question.questionRef,
                    question,
                    subsectionId: subsection.id,
                    sectionId: section.id,
                  });
                }
              }
            }
          }
        }
      }
    }

    return {
      isValid: missingRequiredFields.length === 0,
      missingRequiredFields,
      visibleQuestions,
      requiredQuestions,
    };
  }

  /**
   * Checks if a position (section/subsection indices) is visible
   */
  isPositionVisible(sectionIndex: number, subsectionIndex: number): boolean {
    const section = this.formDefinition.sections[sectionIndex];
    const subsection = section?.subsections[subsectionIndex];

    if (!section || !subsection) return false;

    return this.isSubsectionVisible(subsection, section);
  }
}
